import { memo, useCallback, useEffect, useMemo, useState } from "react";
import Spinner from "~/common/components/Spinner/Spinner";
import { AccordionDetails, Alert } from "~/common/mui";
import type { OrderData, OrderLineItemData } from "~/domains/orders/types/modelsV2";
import type { DocumentListRow } from "~/features/orders/orderList/useOrderListState";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { OrderCardHeader } from "./OrderCardHeader";
import { OrderLineList } from "./OrderLineList";
import {
  OrderCardAccordion,
  OrderCardAccordionSummary,
  StickyCardContainer,
} from "../common/components/v2/OrderAndQuoteSharedStyles";
import { strictIsNonNull } from "~/common/utils/reducers";
import { t } from "i18next";
import { AccordionSummaryToggle } from "../common/components/v2/AccordionSummaryToggle";
import { isErrorEntry } from "../common/utils/util";

interface Props {
  row: DocumentListRow<OrderData>;
  onToggleOpen: (row: DocumentListRow<OrderData>) => void;
  selection: number[];
  onSelectLine: (lineIndex: number) => void;
  onSelectAll: (selectableIndexes: number[], newValue: boolean) => void;
}

export const OrderCard = memo(({ row, onToggleOpen, selection, onSelectLine, onSelectAll }: Props) => {
  const { data, open, loadingDetails, detailsLoaded, errorLoadingDetails } = row;
  const canAddToCart = userHasPermission(Permission.ADD_TO_CART);
  const showCheckboxes = canAddToCart;
  const detailsUnavailable = loadingDetails || !detailsLoaded;
  const lineSelectionDisabled = !open || detailsUnavailable;
  const headerSelectionDisabled = loadingDetails || errorLoadingDetails;
  const [pendingSelectAll, setPendingSelectAll] = useState(false);

  const selectableIndexes = useMemo(() => {
    if (!showCheckboxes || detailsUnavailable) {
      return [];
    }
    return data.products.map((lineItem, index) => (isLineSelectable(lineItem) ? index : null)).filter(strictIsNonNull);
  }, [data.products, detailsUnavailable, showCheckboxes]);

  const noSelectableIndexesAvailable = !detailsUnavailable && selectableIndexes.length === 0;

  const selectableIndexSet = useMemo(() => new Set(selectableIndexes), [selectableIndexes]);

  const isAllSelected = selectableIndexes.length > 0 && selectableIndexes.every((index) => selection.includes(index));

  const handleSelectLine = useCallback(
    (lineIndex: number) => {
      if (!showCheckboxes || lineSelectionDisabled) {
        return;
      }
      if (!selectableIndexSet.has(lineIndex)) {
        return;
      }
      onSelectLine(lineIndex);
    },
    [lineSelectionDisabled, onSelectLine, selectableIndexSet, showCheckboxes],
  );

  const handleSelectAll = useCallback(
    (newValue: boolean) => {
      if (!showCheckboxes) {
        return;
      }
      if (!newValue) {
        setPendingSelectAll(false);
      }
      if (newValue && !open) {
        onToggleOpen(row);
      }
      if (detailsUnavailable) {
        if (newValue) {
          setPendingSelectAll(true);
        } else {
          onSelectAll([], false);
        }
        return;
      }
      if (selectableIndexes.length === 0) {
        setPendingSelectAll(false);
        return;
      }
      onSelectAll(selectableIndexes, newValue);
    },
    [detailsUnavailable, onSelectAll, onToggleOpen, open, row, selectableIndexes, showCheckboxes],
  );

  useEffect(() => {
    if (!pendingSelectAll) {
      return;
    }
    if (detailsUnavailable || selectableIndexes.length === 0) {
      return;
    }
    onSelectAll(selectableIndexes, true);
    setPendingSelectAll(false);
  }, [detailsUnavailable, onSelectAll, pendingSelectAll, selectableIndexes]);

  return (
    <StickyCardContainer open={open}>
      <OrderCardHeader
        row={row}
        selectable={showCheckboxes}
        isAllSelected={isAllSelected}
        onToggleSelectAll={(val) => {
          if (!isErrorEntry(row)) {
            handleSelectAll(val);
          }
        }}
        disabled={!showCheckboxes || headerSelectionDisabled || noSelectableIndexesAvailable}
        sticky={open}
      />

      <OrderCardAccordion
        expanded={open}
        onChange={() => {
          if (!isErrorEntry(row)) {
            onToggleOpen(row);
          }
        }}
      >
        <OrderCardAccordionSummary expandIcon={null}>
          <AccordionSummaryToggle
            open={open}
            showLabel={t("orderList.orderCard.accordionSummary.show")}
            hideLabel={t("orderList.orderCard.accordionSummary.hide")}
            disabled={isErrorEntry(row)}
          />
        </OrderCardAccordionSummary>
        <AccordionDetails sx={{ pb: 0 }}>
          {open && loadingDetails && <Spinner />}
          {open && !loadingDetails && errorLoadingDetails && <Alert severity="error">{t("orderListPage.error")}</Alert>}
          {open && !loadingDetails && !errorLoadingDetails && (
            <OrderLineList
              data={data.products}
              orderData={data}
              soNumber={data.soNumber}
              poNumber={data.poNumber}
              selectable={showCheckboxes}
              selectionDisabled={lineSelectionDisabled}
              selectedIndexes={selection}
              selectItem={handleSelectLine}
              selectableIndexes={selectableIndexes}
              selectableIndexSet={selectableIndexSet}
            />
          )}
        </AccordionDetails>
      </OrderCardAccordion>
    </StickyCardContainer>
  );
});

const isLineSelectable = (line?: OrderLineItemData) => {
  if (!line) {
    return false;
  }
  return Boolean(line.product?.url) && Boolean(line.confirmedQty);
};
